/**
 * POST JSON to a URL with gzip compression applied to the request body.
 *
 * Large CSV payloads compress 10-20× as text, turning a 100 MB body into
 * ~5-10 MB — well under reverse-proxy body-size limits.
 *
 * Express's body-parser (raw-body) automatically decompresses
 * Content-Encoding: gzip request bodies, so no server changes are needed.
 *
 * Falls back to uncompressed JSON when CompressionStream is unavailable
 * (Node.js test environments, very old browsers).
 */
export async function postCompressed(
  url: string,
  data: unknown,
  init: RequestInit = {},
): Promise<Response> {
  const json = JSON.stringify(data);

  if (typeof CompressionStream !== "undefined") {
    try {
      const blob = new Blob([json], { type: "application/json" });
      const compressed = await new Response(
        blob.stream().pipeThrough(new CompressionStream("gzip")),
      ).arrayBuffer();

      return fetch(url, {
        ...init,
        method: init.method ?? "POST",
        headers: {
          ...(init.headers as Record<string, string>),
          "Content-Type": "application/json",
          "Content-Encoding": "gzip",
        },
        body: compressed,
      });
    } catch {
      // fall through to uncompressed
    }
  }

  return fetch(url, {
    ...init,
    method: init.method ?? "POST",
    headers: {
      ...(init.headers as Record<string, string>),
      "Content-Type": "application/json",
    },
    body: json,
  });
}
